import React, { useState, useCallback, useEffect } from 'react';
import './ProductTable.css';

const ProductTable = ({ 
  readOnly = false,
  products, 
  loading, 
  error, 
  updateProduct, 
  revertProduct,
  handleSort, 
  getSortIndicator, 
  isLowStock, 
  formatPrice,
  tableLocked,
  updating,
  clearEditingState = false,
  fieldLabels = {
    productId: 'ID',
    name: 'Nome',
    material: 'Material',
    type: 'Tipo',
    thickness: 'Espessura',
    length: 'Comprimento',
    width: 'Largura',
    price: 'Preço',
    availability: 'Disponibilidade',
    actions: 'Ações'
  }
}) => {

  const [editingCells, setEditingCells] = useState(new Set()); // Track multiple editing cells
  const [editValues, setEditValues] = useState({}); // Track edit values for each cell
  const [originalValues, setOriginalValues] = useState({}); // Track original values for each cell
  const [isInitializing, setIsInitializing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelCellInfo, setCancelCellInfo] = useState(null); // {productId, field}

  // Clear editing state when clearEditingState prop changes
  useEffect(() => {
    if (clearEditingState) {
      setEditingCells(new Set());
      setEditValues({});
      setOriginalValues({});
    }
  }, [clearEditingState]);

  // Check if a cell has changes
  const hasCellChanges = useCallback((productId, field) => {
    const cellKey = `${productId}-${field}`;
    const editValue = editValues[cellKey];
    const originalValue = originalValues[cellKey];
    
    if (editValue === undefined || originalValue === undefined) {
      return false;
    }
    
    // Convert both values to strings for comparison
    const editStr = String(editValue).trim();
    const originalStr = String(originalValue).trim();
    
    const hasChanges = editStr !== originalStr;
    
    return hasChanges;
  }, [editValues, originalValues]);

  // All hooks must be defined before any early returns
  const handleTableClick = useCallback((e) => {
    // If clicking on the table container (not on a cell), cancel any current editing
    if (e.target.classList.contains('table-container') || e.target.classList.contains('table-wrapper')) {
      // Cancel all editing cells
      setEditingCells(new Set());
      setEditValues({});
      setOriginalValues({});
    }
  }, []);

  const handleCellClick = useCallback((productId, field, currentValue) => {
    if (readOnly || field === 'productId' || tableLocked) return;
    
    const cellKey = `${productId}-${field}`;
    
    // If already editing this cell, don't do anything
    if (editingCells.has(cellKey)) return;
    
    setIsInitializing(true);
    
    // Add to editing cells
    setEditingCells(prev => new Set([...prev, cellKey]));
    
    // Set edit value and original value for this cell
    setEditValues(prev => ({
      ...prev,
      [cellKey]: String(currentValue)
    }));
    
    setOriginalValues(prev => ({
      ...prev,
      [cellKey]: currentValue
    }));
    
    // Small delay to prevent immediate blur
    setTimeout(() => {
      setIsInitializing(false);
    }, 100);
  }, [readOnly, tableLocked, editingCells]);

  const handleCellChange = useCallback((e, cellKey) => {
    setEditValues(prev => ({
      ...prev,
      [cellKey]: e.target.value
    }));
  }, []);

  const handleCellSave = useCallback((productId, field) => {
    if (tableLocked) return;
    
    const cellKey = `${productId}-${field}`;
    const editValue = editValues[cellKey];
    
    if (editValue === undefined) return;
    
    let value = editValue;
    
    // Type conversion based on field
    if (field === 'price' || field === 'kgPerMeter' || field === 'kgPerUnit' || field === 'thickness') {
      value = parseFloat(value) || 0;
    } else if (field === 'availability' || field === 'length' || field === 'width') {
      value = parseInt(value) || 0;
    }
    
    updateProduct(productId, field, value);
    
    // Remove from editing cells and clear values
    setEditingCells(prev => {
      const newSet = new Set(prev);
      newSet.delete(cellKey);
      return newSet;
    });
    
    setEditValues(prev => {
      const newValues = { ...prev };
      delete newValues[cellKey];
      return newValues;
    });
    
    setOriginalValues(prev => {
      const newValues = { ...prev };
      delete newValues[cellKey];
      return newValues;
    });
  }, [editValues, updateProduct, tableLocked]);

  // Function to actually cancel cell editing
  const cancelCellEditing = useCallback((productId, field) => {
    const cellKey = `${productId}-${field}`;
    
    // Revert the product data to original value without tracking as a change
    const originalValue = originalValues[cellKey];
    if (originalValue !== undefined) {
      revertProduct(productId, field, originalValue);
    }
    
    // Remove from editing cells and clear values
    setEditingCells(prev => {
      const newSet = new Set(prev);
      newSet.delete(cellKey);
      return newSet;
    });
    
    setEditValues(prev => {
      const newValues = { ...prev };
      delete newValues[cellKey];
      return newValues;
    });
    
    setOriginalValues(prev => {
      const newValues = { ...prev };
      delete newValues[cellKey];
      return newValues;
    });
  }, [originalValues, revertProduct]);

  const handleCellCancel = useCallback((productId, field) => {
    // Don't cancel if we're still initializing
    if (isInitializing) return;
    
    const cellKey = `${productId}-${field}`;
    
    // Only proceed if this cell is currently being edited
    if (editingCells.has(cellKey)) {
      const hasChanges = hasCellChanges(productId, field);
      
      // Check if there are changes
      if (hasChanges) {
        // Show confirmation modal
        setCancelCellInfo({ productId, field });
        setShowCancelModal(true);
      } else {
        // No changes, cancel directly
        cancelCellEditing(productId, field);
      }
    }
  }, [isInitializing, editingCells, hasCellChanges, cancelCellEditing]);

  // Handle confirmation modal actions
  const handleConfirmCancel = useCallback(() => {
    if (cancelCellInfo) {
      cancelCellEditing(cancelCellInfo.productId, cancelCellInfo.field);
    }
    setShowCancelModal(false);
    setCancelCellInfo(null);
  }, [cancelCellInfo, cancelCellEditing]);

  const handleCancelModal = useCallback(() => {
    setShowCancelModal(false);
    setCancelCellInfo(null);
  }, []);

  // Handle hide/show product functionality - direct toggle without modal
  const handleHideProduct = useCallback((productId, isHidden) => {
    updateProduct(productId, 'isHidden', isHidden);
  }, [updateProduct]);


  const handleKeyPress = useCallback((e, productId, field) => {
    if (e.key === 'Enter') {
      handleCellSave(productId, field);
    } else if (e.key === 'Escape') {
      handleCellCancel(productId, field);
    }
  }, [handleCellSave, handleCellCancel]);

  const renderCell = useCallback((product, field) => {
    const cellKey = `${product.productId}-${field}`;
    const isEditing = editingCells.has(cellKey);
    const value = product[field];

    if (isEditing) {
      return (
        <input
          type={field === 'price' || field === 'kgPerMeter' || field === 'kgPerUnit' || field === 'thickness' || field === 'availability' || field === 'length' || field === 'width' ? 'number' : 'text'}
          value={editValues[cellKey] || ''}
          onChange={(e) => handleCellChange(e, cellKey)}
          onKeyDown={(e) => handleKeyPress(e, product.productId, field)}
          onBlur={() => {
            // Don't auto-cancel on blur - let user explicitly cancel with Escape
            // This prevents losing changes when switching between cells
          }}
          onFocus={() => setIsInitializing(false)}
          className="edit-input"
          autoFocus
        />
      );
    }

    let displayValue = value;
    if (field === 'price') {
      displayValue = formatPrice(value);
    }

    return (
      <span 
        className={`cell-content ${field === 'availability' && isLowStock(value) ? 'low-stock' : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleCellClick(product.productId, field, value);
        }}
        title={readOnly ? 'Somente leitura' : 'Clique para editar'}
      >
        {displayValue}
      </span>
    );
  }, [editingCells, editValues, handleCellChange, handleCellClick, handleKeyPress, formatPrice, isLowStock, readOnly]);

  if (loading) {
    return (
      <div className="table-container">
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-container">
        <div className="error-message">
          <p>Error loading products: {error}</p>
        </div>
      </div>
    );
  }


  if (loading) {
    return (
      <div className="table-container">
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-container">
        <div className="error-message">
          <p>Error loading products: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`table-container ${tableLocked ? 'table-locked' : ''}`}
      onClick={handleTableClick}
    >
      {updating && (
        <div className="updating-overlay">
          <div className="spinner"></div>
          <p>Updating products...</p>
        </div>
      )}
      {editingCells.size > 0 && (
        <div className="editing-indicator">
          <span>✏️ Editing {editingCells.size} cell{editingCells.size !== 1 ? 's' : ''} - Press Enter to save, Escape to cancel</span>
        </div>
      )}
      <div className="table-wrapper">
        <table className="product-table">
          <thead className="table-header">
            <tr>
              <th onClick={() => handleSort('productId')} className="sortable">
                {fieldLabels.productId} {getSortIndicator('productId')}
              </th>
              <th onClick={() => handleSort('name')} className="sortable">
                {fieldLabels.name} {getSortIndicator('name')}
              </th>
              <th onClick={() => handleSort('material')} className="sortable">
                {fieldLabels.material} {getSortIndicator('material')}
              </th>
              <th onClick={() => handleSort('type')} className="sortable">
                {fieldLabels.type} {getSortIndicator('type')}
              </th>
              <th onClick={() => handleSort('thickness')} className="sortable">
                {fieldLabels.thickness} {getSortIndicator('thickness')}
              </th>
              <th onClick={() => handleSort('length')} className="sortable">
                {fieldLabels.length} {getSortIndicator('length')}
              </th>
              <th onClick={() => handleSort('width')} className="sortable">
                {fieldLabels.width} {getSortIndicator('width')}
              </th>
              <th onClick={() => handleSort('price')} className="sortable">
                {fieldLabels.price} {getSortIndicator('price')}
              </th>
              <th onClick={() => handleSort('availability')} className="sortable">
                {fieldLabels.availability} {getSortIndicator('availability')}
              </th>
              <th className="action-header">
                {fieldLabels.actions}
              </th>
            </tr>
          </thead>
          <tbody className="table-body">
            {products.map((product) => (
              <tr key={product.productId} className="table-row">
                <td className={`table-cell product-id-cell ${editingCells.has(`${product.productId}-productId`) ? 'editing' : ''}`}>
                  {renderCell(product, 'productId')}
                </td>
                <td className={`table-cell name-cell ${editingCells.has(`${product.productId}-name`) ? 'editing' : ''}`}>
                  {renderCell(product, 'name')}
                </td>
                <td className={`table-cell material-cell ${editingCells.has(`${product.productId}-material`) ? 'editing' : ''}`}>
                  {renderCell(product, 'material')}
                </td>
                <td className={`table-cell type-cell ${editingCells.has(`${product.productId}-type`) ? 'editing' : ''}`}>
                  {renderCell(product, 'type')}
                </td>
                <td className={`table-cell thickness-cell ${editingCells.has(`${product.productId}-thickness`) ? 'editing' : ''}`}>
                  {renderCell(product, 'thickness')}
                </td>
                <td className={`table-cell length-cell ${editingCells.has(`${product.productId}-length`) ? 'editing' : ''}`}>
                  {renderCell(product, 'length')} {product.lengthUnit}
                </td>
                <td className={`table-cell width-cell ${editingCells.has(`${product.productId}-width`) ? 'editing' : ''}`}>
                  {renderCell(product, 'width')} {product.widthUnit}
                </td>
                <td className={`table-cell price-cell ${editingCells.has(`${product.productId}-price`) ? 'editing' : ''}`}>
                  {renderCell(product, 'price')}
                </td>
                <td className={`table-cell availability-cell ${editingCells.has(`${product.productId}-availability`) ? 'editing' : ''}`}>
                  {renderCell(product, 'availability')}
                </td>
                <td className="table-cell action-cell">
                  <button
                    onClick={() => handleHideProduct(product.productId, !product.isHidden)}
                    className={`hide-button ${product.isHidden ? 'hidden' : 'visible'}`}
                    title={product.isHidden ? 'Mostrar produto' : 'Ocultar produto'}
                    disabled={tableLocked}
                  >
                    {product.isHidden ? '🙈' : '👁️'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Confirmation Modal for Cancel */}
      {showCancelModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={handleCancelModal}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', color: '#2c3e50' }}>Descartar Alterações?</h3>
            <p style={{ margin: '0 0 24px 0', color: '#7f8c8d' }}>
              Você tem alterações não salvas nesta célula. Tem certeza de que deseja descartá-las?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={handleCancelModal}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  backgroundColor: '#ecf0f1',
                  color: '#7f8c8d',
                  border: '1px solid #bdc3c7',
                  cursor: 'pointer'
                }}
              >
                Continuar Editando
              </button>
              <button
                onClick={handleConfirmCancel}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Descartar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTable;
